/**
 * Account copy lookup.
 *
 * Reads the strings PHP emits as accountData.i18n (Account_Helper::wallet_i18n() and
 * ::auth_i18n(), localized by both class-account.php and class-buttons-account-login.php).
 *
 * WHY THE ENGLISH IS NOT KEPT HERE AS A PER-STRING FALLBACK. The pattern used elsewhere in
 * this theme is `window.accountData?.x || 'English sentence'`, which reads as safe and is the
 * reason the deposit and withdrawal screens were never translatable: the `||` branch is what
 * the user actually gets, it never passes through gettext, and the literal stays in the
 * bundle. Searching the catalog for one of those sentences finds it — emitted from PHP
 * somewhere else entirely — so the string looks handled while the money path still shows
 * English. See L1_LOCALIZATION_AUDIT.md §4.5a.
 *
 * So the sweep removes the literals rather than moving them, and the check that this worked
 * is "no English remains at the call site" (verification 11), not "the string is in the .pot".
 *
 * THE ONE REMAINING LITERAL IS DELIBERATE. It is a single last-resort string in one place
 * instead of twenty scattered through the flow, and it is unreachable in practice: these
 * files already read window.accountData.ajaxUrl and .nonce, so an absent accountData means
 * the deposit flow cannot submit at all. Depending on it for copy adds no failure mode that
 * was not already fatal.
 *
 * @package betbeast
 */

const MISSING_COPY = 'Something went wrong. Please try again.';

/**
 * Look up an account string by key.
 *
 * Placeholders are {min} / {max} rather than printf, matching Validation_Rules' error
 * messages, so translators meet one placeholder style across the product.
 *
 * @param {string} key          Key from Account_Helper::wallet_i18n() / ::auth_i18n().
 * @param {Object} replacements Placeholder values, e.g. { min: 20 }.
 * @return {string}
 */
export function t(key, replacements = {}) {
    return lookup('accountData', window.accountData && window.accountData.i18n, key, replacements);
}

/**
 * Bind a lookup to a different localized object.
 *
 * `accountData` is emitted only by the account and auth blocks, so a block outside that
 * surface — the search block, the sportsbook, the header — cannot use t(). Each of those
 * already localizes an object of its own (myScriptData, sportsbookData, appData), and this
 * binds the same lookup to whichever one the caller's block actually emits.
 *
 * Passing a getter rather than the object itself matters: modules are evaluated at import
 * time, before wp_localize_script's inline script has run, so reading the global eagerly
 * would capture undefined for the life of the page.
 *
 * @param {string}   name   Global name, for the warning message only.
 * @param {Function} getter Returns the dictionary, called on every lookup.
 * @return {Function} A t()-shaped function.
 */
export function createT(name, getter) {
    return (key, replacements = {}) => lookup(name, getter(), key, replacements);
}

function lookup(name, dictionary, key, replacements) {
    const dict = (typeof window !== 'undefined' && dictionary) || {};
    let text = typeof dict[key] === 'string' && dict[key] !== '' ? dict[key] : '';

    if (!text) {
        console.warn(`[account-i18n] missing key "${key}" — ${name}.i18n not localized?`);
        text = MISSING_COPY;
    }

    for (const nameKey of Object.keys(replacements)) {
        text = text.split(`{${nameKey}}`).join(String(replacements[nameKey]));
    }

    return text;
}
